// Early access: the waitlist form, posting to POST /api/waitlist (src/worker/Api.ts).
// Client checks mirror the server's rules, so a 400 is rare; every server answer gets its own message
// in the form's polite status region.

/** GitHub's rule, as the server checks it: 1-39 alphanumerics and single inner hyphens, optional @. */
const GITHUB = /^@?[a-z\d](?:[a-z\d]|-(?=[a-z\d])){0,38}$/i
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

type Outcome =
  | { readonly kind: "joined" | "updated" }
  | { readonly kind: "invalid" | "limited" | "unavailable" }

const listing = (names: ReadonlyArray<string>) =>
  names.length <= 1 ? (names[0] ?? "") : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`

async function send(payload: Record<string, unknown>): Promise<Outcome> {
  try {
    const response = await fetch("/api/waitlist", {
      method: "POST",
      headers: { "content-type": "application/json", accept: "application/json" },
      body: JSON.stringify(payload),
    })
    if (response.ok) {
      const body = (await response.json().catch(() => null)) as { status?: unknown } | null
      return { kind: body?.status === "updated" ? "updated" : "joined" }
    }
    if (response.status === 400) return { kind: "invalid" }
    if (response.status === 429) return { kind: "limited" }
    return { kind: "unavailable" }
  } catch {
    return { kind: "unavailable" }
  }
}

export function initWaitlist(): void {
  const form = document.querySelector<HTMLFormElement>("form.waitlist")
  if (!form) return
  const em = form.querySelector<HTMLInputElement>("#f-email")
  const gh = form.querySelector<HTMLInputElement>("#f-gh")
  const hp = form.querySelector<HTMLInputElement>("#f-company")
  const done = form.querySelector<HTMLElement>(".done")
  const button = form.querySelector<HTMLButtonElement>('button[type="submit"]')
  const label = button?.querySelector(".lbl")
  if (!em || !gh || !done || !button) return
  const idle = label?.textContent ?? "Join the waitlist"
  const pills = [...form.querySelectorAll<HTMLElement>(".pill-o")]
  for (const p of pills) {
    p.addEventListener("click", () =>
      p.setAttribute("aria-pressed", p.getAttribute("aria-pressed") === "true" ? "false" : "true"),
    )
  }
  const pre = new URLSearchParams(location.search).get("app")
  if (pre) form.querySelector(`.pill-o[data-app="${CSS.escape(pre)}"]`)?.setAttribute("aria-pressed", "true")

  const setErr = (input: HTMLInputElement, msg: string) => {
    input.setAttribute("aria-invalid", msg ? "true" : "false")
    const id = input.getAttribute("aria-describedby")
    const slot = id ? document.getElementById(id) : null
    if (slot) slot.textContent = msg
    return !msg
  }
  const say = (text: string, { fail = false, retry = false } = {}) => {
    done.classList.toggle("fail", fail)
    done.textContent = text
    if (retry) {
      const again = document.createElement("button")
      again.type = "button"
      again.className = "retry"
      again.textContent = "Try again"
      again.addEventListener("click", () => form.requestSubmit())
      done.append(again)
    }
    done.hidden = false
    done.focus()
  }
  const busy = (on: boolean) => {
    button.disabled = on
    if (on) button.setAttribute("aria-busy", "true")
    else button.removeAttribute("aria-busy")
    if (label) label.textContent = on ? "Joining…" : idle
  }

  let sending = false
  form.addEventListener("submit", async (e) => {
    e.preventDefault()
    if (sending) return
    const email = em.value.trim()
    const github = gh.value.trim()
    const okEmail = setErr(
      em,
      email && EMAIL.test(email) && email.length <= 254 ? "" : "Enter an email, like you@company.com.",
    )
    const okGitHub = setErr(
      gh,
      !github || GITHUB.test(github) ? "" : "Letters, numbers and single hyphens only.",
    )
    if (!okEmail || !okGitHub) {
      ;(okEmail ? gh : em).focus()
      return
    }
    const chosen = pills.filter((p) => p.getAttribute("aria-pressed") === "true")
    const apps = chosen.map((p) => p.dataset.app).filter((slug): slug is string => Boolean(slug))
    const names = chosen.map((p) => (p.textContent ?? "").trim())

    sending = true
    busy(true)
    const outcome = await send({
      email,
      ...(github ? { github } : {}),
      apps,
      source: location.pathname,
      company: hp?.value ?? "",
    })
    sending = false
    busy(false)

    switch (outcome.kind) {
      case "joined":
        say(
          `You're on the list${names.length ? ` for ${listing(names)}` : ""}. One email when your apps are ready.`,
        )
        break
      case "updated":
        say(
          names.length
            ? "You're already on the list; we've added these apps."
            : "You're already on the list.",
        )
        break
      case "invalid":
        say("Something in the form doesn't look right. Check your email and GitHub handle.", { fail: true })
        break
      case "limited":
        say("Too many tries, wait a minute.", { fail: true })
        break
      case "unavailable":
        say("We couldn't save that just now.", { fail: true, retry: true })
        break
    }
  })
}
